import React from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
} from '@mui/material';
import {
  TrendingUp,
  People,
  AccessTime,
  AttachMoney,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => (
  <Card>
    <CardContent sx={{ p: '20px !important' }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography
            variant="caption"
            sx={{ color: '#6b7194', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.7rem' }}
            gutterBottom
          >
            {title}
          </Typography>
          <Typography variant="h4" component="div" fontWeight={700} sx={{ color: '#1a1d2e', lineHeight: 1.2, mt: 0.5 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: '#3ecf8e', fontWeight: 600, mt: 1, display: 'block' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            background: `linear-gradient(135deg, ${color}22 0%, ${color}44 100%)`,
            borderRadius: 3,
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  // Data for Prevalence Chart
  const prevalenceData = [
    { condition: 'Fibromyalgia',  patients: 4200, color: '#5b6af0' },
    { condition: 'CFS/ME',        patients: 3800, color: '#9b59b6' },
    { condition: 'Endometriosis', patients: 3200, color: '#3ecf8e' },
    { condition: 'Autoimmune',    patients: 2900, color: '#f4a03a' },
    { condition: 'POTS',          patients: 2100, color: '#e05260' },
    { condition: 'Long COVID',    patients: 1800, color: '#0bc5ea' },
  ];

  // Data for Gender Disparity Chart
  const genderData = [
    { gender: 'Women', avgDelay: 547, color: '#e05260' },
    { gender: 'Men',   avgDelay: 318, color: '#5b6af0' },
  ];

  // Data for Geographic Distribution (simplified US regions)
  const geographicData = [
    { region: 'Northeast', delay: 523, prevalence: 3.5 },
    { region: 'Southeast', delay: 601, prevalence: 2.9 },
    { region: 'Midwest', delay: 578, prevalence: 3.1 },
    { region: 'Southwest', delay: 612, prevalence: 2.7 },
    { region: 'West', delay: 487, prevalence: 3.2 },
  ];

  const COLORS = ['#5b6af0', '#9b59b6', '#3ecf8e', '#f4a03a', '#e05260', '#0bc5ea'];

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight={700} sx={{ color: '#1a1d2e' }}>
          InvisibleIllness.AI Dashboard
        </Typography>
        <Typography variant="body1" sx={{ color: '#6b7194' }}>
          Making invisible illnesses visible through data and AI
        </Typography>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Patients Analyzed"    value="125,847" icon={<People />}      color="#5b6af0" subtitle="+12% from last month" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Avg. Diagnostic Delay" value="547 days" icon={<AccessTime />} color="#e05260" subtitle="18 months average" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Economic Impact"       value="$5.2B"    icon={<AttachMoney />} color="#3ecf8e" subtitle="Annual burden" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Risk Assessments"      value="8,432"    icon={<TrendingUp />} color="#f4a03a" subtitle="This month" />
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Prevalence by Condition */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '400px' }}>
            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ color: '#1a1d2e' }}>
              Prevalence by Invisible Illness
            </Typography>
            <ResponsiveContainer width="100%" height="90%">
              <PieChart>
                <Pie
                  data={prevalenceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ condition, percent }) => `${condition}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="patients"
                >
                  {prevalenceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Geographic Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '400px' }}>
            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ color: '#1a1d2e' }}>
              Geographic Distribution — Diagnostic Delays
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={geographicData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eceef8" />
                <XAxis dataKey="region" tick={{ fill: '#6b7194', fontSize: 12 }} />
                <YAxis label={{ value: 'Days', angle: -90, position: 'insideLeft', fill: '#6b7194' }} tick={{ fill: '#6b7194', fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #eceef8', boxShadow: '0 4px 12px rgba(91,106,240,0.1)' }} />
                <Legend />
                <Bar dataKey="delay" fill="#5b6af0" name="Avg Delay (days)" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Gender Disparity Analysis */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '400px' }}>
            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ color: '#1a1d2e' }}>
              Gender Disparity in Diagnosis
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={genderData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#eceef8" />
                <XAxis type="number" label={{ value: 'Days to Diagnosis', position: 'insideBottom', offset: -5, fill: '#6b7194' }} tick={{ fill: '#6b7194', fontSize: 12 }} />
                <YAxis type="category" dataKey="gender" tick={{ fill: '#6b7194', fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #eceef8', boxShadow: '0 4px 12px rgba(91,106,240,0.1)' }} />
                <Legend />
                <Bar dataKey="avgDelay" name="Avg Delay (days)" radius={[0,6,6,0]}>
                  {genderData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Key Insights */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '400px' }}>
            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ color: '#1a1d2e' }}>
              Key Insights
            </Typography>
            <Box sx={{ mt: 2 }}>
              {[
                { text: 'Women wait 72% longer for diagnosis',   sub: 'Average 547 days vs 318 days for men',                    bg: 'rgba(91,106,240,0.06)',  dot: '#5b6af0' },
                { text: 'Rural areas show 45% longer delays',    sub: 'Limited specialist access is primary factor',             bg: 'rgba(224,82,96,0.06)',   dot: '#e05260' },
                { text: 'Early intervention saves $13K/patient', sub: 'Reduced hospitalizations and lost productivity',          bg: 'rgba(62,207,142,0.06)',  dot: '#3ecf8e' },
                { text: 'AI identifies 5 symptom subtypes',      sub: 'Pattern recognition improves diagnostic accuracy by 40%', bg: 'rgba(244,160,58,0.06)',  dot: '#f4a03a' },
              ].map((item, i) => (
                <Box key={i} sx={{ mb: 1.5, p: 2, backgroundColor: item.bg, borderRadius: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: item.dot, mt: '5px', flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#1a1d2e' }}>
                      {item.text}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6b7194' }}>
                      {item.sub}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;

// Made with Bob
